import axios, { type AxiosResponse, type AxiosInstance, CanceledError } from 'axios';
import { isNil, keys } from 'lodash';
// Import Types
import { ResponseModel } from 'types';
// Import Store
import { store } from 'store';
import { doLogout, updateToken } from 'store/slices/auth';
import { clearMaintenanceSettings, setMaintenanceSettings } from 'store/slices/maintenance';
// Import Constants
import { DefaultLangId } from 'constants/common';
import { BrowserStorageKeys } from 'constants/browserStorage';
// Import Helpers
import { replaceDomainFromEnvironmentURL } from 'services/Helpers';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CancelableRequest<T = any, P = AnyObject> = {
    cancel: (reason?: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    request: (requestBody?: any, params?: P) => Promise<ResponseModel<T>>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SimpleCancelableRequest<T = any, P = AnyObject> = {
    cancel: (reason?: string) => void;
    request: (params?: P) => Promise<ResponseModel<T>>;
};

const BackofficeRequestService = axios.create({
    baseURL: process.env.REACT_APP_BACKOFFICE_URL, // TODO: refactor lang to var
    timeout: 20000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

const CRMRequestServiceMetaData = {
    baseURL: process.env.REACT_APP_CRM_URL,
    timeout: 200000,
    headers: {
        'Content-Type': 'application/json',
    },
};

const CRMRequestServiceDownloadMetaData = {
    baseURL: process.env.REACT_APP_CRM_DOWNLOAD_URL,
    timeout: 200000,
    headers: {
        'Content-Type': 'application/json',
    },
};

const CRMGalleryRequestService = axios.create({
    baseURL: process.env.REACT_APP_CRM_GALLERY_URL,
    timeout: 200000,
});

const CRMRequestService = axios.create({ ...CRMRequestServiceMetaData });
const CRMRequestDownloadLinkService = axios.create({ ...CRMRequestServiceDownloadMetaData });
const CRMRequestServiceDownload = axios.create({ ...CRMRequestServiceDownloadMetaData, responseType: 'blob' });

const setRequestBaseURLInterceptor = (request) => {
    request.baseURL = replaceDomainFromEnvironmentURL(request.baseURL);
    return request;
};

const setRequestBaseURLInterceptorErrHandler = (error) => {
    return Promise.reject(error);
};

const setRequestHeadersInterceptor = (request) => {
    const state = store.getState();
    const { accessToken } = state.auth;
    if (accessToken) {
        request.headers.Authentication = accessToken;
    }
    return request;
};

const setRequestHeadersBackofficeInterceptor = (request) => {
    return request;
};

const CRMBaseURLInterceptor = (request) => {
    const state = store.getState();
    const Language = state.userData.data?.Settings?.Language ?? DefaultLangId;
    request.baseURL += `${Language}/`;
    return request;
};

const setRequestHeadersInterceptorErrHandler = (error) => {
    // need handle error case

    return Promise.reject(error);
};

const responseInterceptor = (response) => {
    const { headers } = response;
    const token = headers.authentication;
    const maintenanceStartDate = headers['x-crm-maintenance-startdate'];
    const maintenanceEndDate = headers['x-crm-maintenance-enddate'];
    const activePartnerId = headers['x-crm-activepartnerid'];

    if (!isNil(token)) {
        localStorage.setItem(BrowserStorageKeys.accessToken, token);
        store.dispatch(updateToken(token));
    }

    // We update maintenance state from requests, where we have received ['x-crm-activepartnerid'] header
    if (!isNil(activePartnerId)) {
        const { maintenance } = store.getState();
        const shouldUpdateMaintenance =
            maintenanceStartDate !== maintenance.startDate || maintenanceEndDate !== maintenance.endDate;

        if (shouldUpdateMaintenance) {
            if (isNil(maintenanceStartDate) || isNil(maintenanceEndDate)) {
                store.dispatch(clearMaintenanceSettings());
            } else {
                store.dispatch(
                    setMaintenanceSettings({
                        startDate: maintenanceStartDate,
                        endDate: maintenanceEndDate,
                    }),
                );
            }
        }
    }

    return response;
};

const responseInterceptorErrHandler = (error) => {
    // Unauthorized 401
    // Forbidden 403

    // Don't handle cancelled requests - let them propagate
    if (error instanceof CanceledError) {
        return Promise.reject(error);
    }

    // Check if error has a response (server responded with error status)
    if (error.response) {
        const { status } = error.response;

        if (status === 401) {
            localStorage.removeItem(BrowserStorageKeys.accessToken);
            store.dispatch(doLogout());
        }
    } else {
        // Network error or request setup error (no response received)
        // TODO: should redirected to the no internet page.
        // eslint-disable-next-line no-console
        console.log('Network error');
    }

    return Promise.reject(error);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const responseDTOParserInterceptor = (response: AxiosResponse<any>) => {
    const { data, headers } = response;
    return {
        data,
        headers,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as AxiosResponse<any>;
};

const responseDTOParserInterceptorErrHandler = (error) => {
    // need handle error case

    return Promise.reject(error);
};

const CRMResponseDTOParserInterceptor = (response) => {
    return response.data;
};

const CRMResponseDTOParserInterceptorErrHandler = (error) => {
    // need handle error case
    return Promise.reject(error);
};

// Request interceptors ===========================================
BackofficeRequestService.interceptors.request.use(setRequestBaseURLInterceptor, setRequestBaseURLInterceptorErrHandler);
CRMGalleryRequestService.interceptors.request.use(setRequestBaseURLInterceptor, setRequestBaseURLInterceptorErrHandler);
CRMRequestService.interceptors.request.use(setRequestBaseURLInterceptor, setRequestBaseURLInterceptorErrHandler);
CRMRequestDownloadLinkService.interceptors.request.use(
    setRequestBaseURLInterceptor,
    setRequestBaseURLInterceptorErrHandler,
);
CRMRequestServiceDownload.interceptors.request.use(
    setRequestBaseURLInterceptor,
    setRequestBaseURLInterceptorErrHandler,
);

// Request interceptors ===========================================
BackofficeRequestService.interceptors.request.use(
    setRequestHeadersBackofficeInterceptor,
    setRequestHeadersInterceptorErrHandler,
);
CRMGalleryRequestService.interceptors.request.use(setRequestHeadersInterceptor, setRequestHeadersInterceptorErrHandler);
CRMRequestService.interceptors.request.use(setRequestHeadersInterceptor, setRequestHeadersInterceptorErrHandler);
CRMRequestService.interceptors.request.use(CRMBaseURLInterceptor, setRequestHeadersInterceptorErrHandler);
CRMRequestDownloadLinkService.interceptors.request.use(
    setRequestHeadersInterceptor,
    setRequestHeadersInterceptorErrHandler,
);
CRMRequestDownloadLinkService.interceptors.request.use(CRMBaseURLInterceptor, setRequestHeadersInterceptorErrHandler);

CRMRequestServiceDownload.interceptors.request.use(
    setRequestHeadersInterceptor,
    setRequestHeadersInterceptorErrHandler,
);
CRMRequestServiceDownload.interceptors.request.use(CRMBaseURLInterceptor, setRequestHeadersInterceptorErrHandler);

// Response interceptors ===========================================
BackofficeRequestService.interceptors.response.use(responseInterceptor, responseInterceptorErrHandler);
BackofficeRequestService.interceptors.response.use(
    responseDTOParserInterceptor,
    responseDTOParserInterceptorErrHandler,
);
CRMGalleryRequestService.interceptors.response.use(responseInterceptor, responseInterceptorErrHandler);
CRMGalleryRequestService.interceptors.response.use(
    CRMResponseDTOParserInterceptor,
    CRMResponseDTOParserInterceptorErrHandler,
);

CRMRequestService.interceptors.response.use(responseInterceptor, responseInterceptorErrHandler);
CRMRequestService.interceptors.response.use(CRMResponseDTOParserInterceptor, CRMResponseDTOParserInterceptorErrHandler);
CRMRequestDownloadLinkService.interceptors.response.use(responseInterceptor, responseInterceptorErrHandler);
CRMRequestDownloadLinkService.interceptors.response.use(
    CRMResponseDTOParserInterceptor,
    CRMResponseDTOParserInterceptorErrHandler,
);

CRMRequestServiceDownload.interceptors.response.use(responseInterceptor, responseInterceptorErrHandler);
CRMRequestServiceDownload.interceptors.response.use(
    CRMResponseDTOParserInterceptor,
    CRMResponseDTOParserInterceptorErrHandler,
);

const getEncodedQueryParams = (queryParams) => {
    return keys(queryParams).reduce((acc, key) => {
        acc[key] = encodeURIComponent(queryParams[key]);
        return acc;
    }, {});
};

const getEndpointPath = (endpointPath, queryParams, uriEncoding) => {
    return endpointPath(uriEncoding ? getEncodedQueryParams(queryParams) : queryParams);
};

/**
 * Tracks the in-flight calls of one request factory. Every call gets its own AbortController,
 * so cancelling aborts the calls that are running now without disabling the calls made afterwards.
 */
const createAbortScope = () => {
    const controllers = new Set<AbortController>();

    const run = <R>(executor: (signal: AbortSignal) => Promise<R>): Promise<R> => {
        const controller = new AbortController();
        controllers.add(controller);

        return executor(controller.signal).finally(() => {
            controllers.delete(controller);
        });
    };

    const cancel = (reason?: string): void => {
        controllers.forEach((controller) => controller.abort(reason));
        controllers.clear();
    };

    return { run, cancel };
};

// ================= GET =================
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getCancelableRequest = <T = any, P = AnyObject>({
    requestService,
    endpointPath,
    defaultParams = {},
    uriEncoding = true,
}: {
    requestService: AxiosInstance;
    endpointPath: (params: P) => string;
    defaultParams?: Partial<P>;
    uriEncoding?: boolean;
}): SimpleCancelableRequest<T, P> => {
    const scope = createAbortScope();

    const request = (params: Partial<P> = {} as Partial<P>): Promise<ResponseModel<T>> => {
        const url = getEndpointPath(endpointPath, { ...defaultParams, ...params }, uriEncoding);
        return scope.run((signal) => requestService.get(url, { signal }));
    };

    return {
        cancel: scope.cancel,
        request: request as SimpleCancelableRequest<T, P>['request'],
    };
};

// ================= POST =================
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const postCancelableRequest = <T = any, P = AnyObject>({
    requestService,
    endpointPath,
    defaultParams = {},
    uriEncoding = true,
}: {
    requestService: AxiosInstance;
    endpointPath: (params: P) => string;
    defaultParams?: Partial<P>;
    uriEncoding?: boolean;
}): CancelableRequest<T, P> => {
    const scope = createAbortScope();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const request = (requestBody?: any, params: Partial<P> = {} as Partial<P>): Promise<ResponseModel<T>> => {
        const url = getEndpointPath(endpointPath, { ...defaultParams, ...params }, uriEncoding);
        return scope.run((signal) => requestService.post(url, requestBody, { signal }));
    };

    return {
        cancel: scope.cancel,
        request: request as CancelableRequest<T, P>['request'],
    };
};

// ================= DELETE =================
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const deleteCancelableRequest = <T = any, P = AnyObject>({
    requestService,
    endpointPath,
    defaultParams = {},
    uriEncoding = true,
}: {
    requestService: AxiosInstance;
    endpointPath: (params: P) => string;
    defaultParams?: Partial<P>;
    uriEncoding?: boolean;
}): SimpleCancelableRequest<T, P> => {
    const scope = createAbortScope();

    const request = (params: Partial<P> = {} as Partial<P>): Promise<ResponseModel<T>> => {
        const url = getEndpointPath(endpointPath, { ...defaultParams, ...params }, uriEncoding);
        return scope.run((signal) => requestService.delete(url, { signal }));
    };

    return {
        cancel: scope.cancel,
        request: request as SimpleCancelableRequest<T, P>['request'],
    };
};

export {
    BackofficeRequestService,
    CRMRequestService,
    CRMGalleryRequestService,
    CRMRequestServiceDownload,
    CRMRequestDownloadLinkService,
    getCancelableRequest,
    postCancelableRequest,
    deleteCancelableRequest,
    replaceDomainFromEnvironmentURL,
};
